import type { TippyProps } from '@tippyjs/react';
import type { ReactNode, CSSProperties, ElementType } from 'react';

import { useCallback, useMemo, useRef } from 'react';

import useClassy from '@core/hooks/useClassy';
import pxOrCssValue from '@core/utils/pxOrCssValue';
import Tooltip from '@ui/Tooltip';

import classes from './index.module.scss';

const TOOLTIP_DELAY: TippyProps['delay'] = [500, null];
const TOOLTIP_OFFSET: TippyProps['offset'] = [0, 5];

interface TruncateProps {
  /**
   * The text to truncate
   */
  children: ReactNode;
  /**
   * Additional class names to apply to the truncated element
   */
  className?: string;
  /**
   * Limits text to a specific number of lines, truncating with an ellipsis only
   * if the text exceeds that limit. When omitted, text will be truncated
   * normally to a single line.
   */
  lineClamp?: number;
  /**
   * The CSS max-width value of the truncated element. Provide a number to use pixels, or a string to use any
   * valid CSS max-width value.
   */
  maxWidth?: CSSProperties['maxWidth'];
  /**
   * The HTML tag to render the truncated text as. Defaults to `span`.
   */
  Tag?: ElementType;
  /**
   * Additional props forwarded to the underlying Tooltip (Tippy). Use this as
   * an escape hatch for Tippy options not exposed by Truncate directly — e.g.
   * `appendTo` when the tooltip needs to inherit styles from a specific ancestor.
   */
  tippyProps?: Omit<TippyProps, 'content' | 'children'>;
}

/**
 * Truncate a text node with an ellipsis when the text node overflows its container
 */
export default function Truncate({
  children,
  className,
  tippyProps,
  lineClamp,
  maxWidth = '100%',
  Tag = 'span',
}: TruncateProps) {
  const ref = useRef<HTMLElement>(null);
  const bem = useClassy(classes, 'Truncate');
  const styles = useMemo(
    () =>
      ({
        '--Truncate-line-clamp': lineClamp,
        '--Truncate-max-width': pxOrCssValue(maxWidth),
      }) as CSSProperties,
    [lineClamp, maxWidth],
  );

  /**
   * Determines if the text is truncated by comparing the offset dimensions (the
   * visible size of the element) to the scroll dimensions (the total size of
   * the content). If the scroll dimensions are larger than the offset
   * dimensions, it means that some content is hidden and thus truncated.
   */
  const isTruncated = useCallback(() => {
    if (!ref.current) return false;
    if (lineClamp) {
      // When line clamping, we need to check height instead of width to
      // determine if the text is truncated, since text overflows vertically.
      return ref.current.offsetHeight < ref.current.scrollHeight;
    }
    return ref.current.offsetWidth < ref.current.scrollWidth;
  }, [lineClamp]);

  return (
    <Tooltip
      arrow={false}
      asTitle
      content={children}
      delay={TOOLTIP_DELAY}
      offset={TOOLTIP_OFFSET}
      onShow={() => {
        return isTruncated()
          ? undefined // Void return value allows the tooltip to show
          : false; // Cancel the tooltip if the text is not truncated
      }}
      placement="bottom"
      {...tippyProps}
    >
      <Tag ref={ref} className={bem('&', className, lineClamp && '_line-clamp')} style={styles}>
        {children}
      </Tag>
    </Tooltip>
  );
}
